/**
 * 좌석 참여자 등급 판정 — 4등급 권한 게이트 (menu #816 M2 / admin #2206)
 *
 * ## 판정의 단일 출처는 admin 이다
 *
 * `App\Enums\ComensalRole` 의 술어를 그대로 옮긴다. FE 가 자기 규칙을 만들면 화면이
 * 허용하는 것과 서버가 허용하는 것이 갈리고, 그 차이는 **누를 수 있는데 403 이 나는
 * 버튼**으로 나타난다.
 *
 * ## 왜 스캔만으로는 아무것도 못 하는가
 *
 * QR 은 사진 한 장으로 퍼진다. 그래서 스캔이 도달하는 최대 권한을 `invitado`
 * (메뉴 열람만)로 두고, 장바구니·주문·지분 claim 은 **합류 후**로 미룬다(D2).
 *
 * 프로토타입은 Invitado 에게 장바구니까지 허용했으나 2026-07-26 결정으로 강화됐다.
 */

/** admin `ComensalRole` 과 값이 일치해야 한다. */
export type ComensalRole = 'invitado' | 'comensal' | 'anfitrion' | 'personal';

/** admin `ComensalJoinStatus` 와 값이 일치해야 한다. */
export type ComensalJoinStatus = 'pending' | 'joined' | 'rejected' | 'left';

/**
 * 권한 서열 — `Invitado → Comensal → Anfitrión` 은 포함 관계다.
 *
 * `Personal`(직원)은 그 축과 별개이지만 운영 권한이 가장 넓으므로 최상위에 둔다.
 */
const ROLE_RANK: Record<ComensalRole, number> = {
  invitado: 0,
  comensal: 1,
  anfitrion: 2,
  personal: 3,
};

function rankOf(role: string): number {
  return role in ROLE_RANK ? ROLE_RANK[role as ComensalRole] : -1;
}

/**
 * 이 등급이 요구 등급 이상인가.
 *
 * 모르는 값은 **가장 낮게** 본다 — 서버가 새 등급을 추가했을 때 FE 가 그것을
 * 특권으로 오인하면 안 된다(fail-closed).
 */
export function atLeastRole(role: string | null | undefined, required: ComensalRole): boolean {
  if (role === null || role === undefined) {
    return false;
  }

  return rankOf(role) >= ROLE_RANK[required];
}

/** 합류했는가 — `invitado` 만 거짓이다. */
export function hasJoined(role: string | null | undefined): boolean {
  return atLeastRole(role, 'comensal');
}

/** 합류 요청을 승인할 수 있는가 — 대표(anfitrión) 또는 직원. */
export function canApproveJoin(role: string | null | undefined): boolean {
  return role === 'anfitrion' || role === 'personal';
}

/**
 * 장바구니·주문을 쓸 수 있는가 (D2).
 *
 * 스캔만 한 사람은 메뉴만 본다. 이 판정이 `hasJoined` 와 같은 이유는 **지금은** 같기
 * 때문이고, 두 질문이 갈릴 수 있으므로 이름을 나눠 둔다.
 */
export function canUseCart(role: string | null | undefined): boolean {
  return hasJoined(role);
}

/**
 * 지분을 가져올 수 있는가.
 *
 * 등급뿐 아니라 **합류 상태**도 본다 — `pending`(승인 대기)은 아직 아니고, `left`
 * (이탈)는 더 이상 아니다. 등급만 보면 승인 전에 claim 버튼이 열린다.
 */
export function canClaimShare(
  role: string | null | undefined,
  joinStatus: string | null | undefined
): boolean {
  return hasJoined(role) && joinStatus === 'joined';
}
